/**
 * Backend-accepted role enum (mirror of ROLE_ENUM_VALUES in
 * backend/src/api/routes/users.ts). Used by admin user-edit forms to strip
 * legacy values (e.g. pre-Phase-27 'employee', service-account roles) before
 * PUT so the backend doesn't reject the save with "body/roles/0 must be equal
 * to one of the allowed values".
 *
 * Per Petra 2026-05-21 staging UAT: contact-info fields (slackUserId,
 * phoneNumber) shouldn't depend on roles validation passing. Long-term fix
 * is a separate /admin/users/:id/contact endpoint (tracked in v7.0 quick
 * backlog); this strip is the short-term workaround.
 */
export const BACKEND_ROLE_ENUM = [
  'admin',
  'manager',
  'task_manager',
  'client_manager',
  'finance',
  'hr',
  'sales',
  'marketing',
  'isms_manager',
  'user',
] as const;

/** Dropdown options for role selection in admin forms (Phase 27 D-04). */
export const availableRoles: { value: string; label: string }[] = [
  { value: 'user', label: 'User' },                           // RENAMED from 'employee' (D-05)
  { value: 'admin', label: 'Administrator' },
  { value: 'manager', label: 'Project Manager' },
  { value: 'task_manager', label: 'Task Manager' },
  { value: 'client_manager', label: 'Client Manager' },       // NEW (D-04)
  { value: 'finance', label: 'Finance' },                     // NEW (D-04)
  { value: 'hr', label: 'HR' },                               // REPLACES happy_maker (D-06)
  { value: 'sales', label: 'Sales' },
  { value: 'marketing', label: 'Marketing' },                 // NEW (D-04)
  { value: 'isms_manager', label: 'ISMS Manager' },           // NEW (D-04)
  // Legacy/system role values intentionally retained — they appear in DB for
  // service accounts and we do not want admin UI to silently drop them when
  // editing such a user (Open Question #1 → keep defensively).
  { value: 'system_administrator', label: 'System Administrator' },
  { value: 'robot', label: 'Robot' },
];

/** Map legacy role names (from Rails DB) to normalized names. */
export const roleAliasMap: Record<string, string> = {
  administrator: 'admin',
  project_manager: 'manager',
  // 'user → employee' alias REMOVED (D-05: 'user' is now the canonical value).
  // 'happy_maker' alias REMOVED (D-06: merged into 'hr' by SQL migration).
};

/** Normalize a single role string, mapping legacy names to canonical ones. */
export function normalizeRole(role: string): string {
  return roleAliasMap[role] ?? role;
}

/** Normalize a list of roles, deduplicating after alias resolution. */
export function normalizeRoles(roles: string[]): string[] {
  return [...new Set(roles.map(normalizeRole))];
}
